<script setup>
import { ref } from "vue";
import { CLOUD_STORAGE } from "../../../core/constants.js";
import { useCloudStorage } from "../../composables/useCloudStorage.js";

const emit = defineEmits(["close"]);
const { connect } = useCloudStorage();

const PROVIDERS = [
  { type: CLOUD_STORAGE.GOOGLE_DRIVE, title: "Google Drive" },
  { type: CLOUD_STORAGE.ONE_DRIVE, title: "OneDrive" },
  { type: CLOUD_STORAGE.BOX_DRIVE, title: "Box" },
  { type: CLOUD_STORAGE.DROPBOX_DRIVE, title: "Dropbox" },
];

const isConnecting = ref(false);
async function onSelect(provider) {
  if (isConnecting.value) return;
  isConnecting.value = true;
  try {
    // Redirects the whole page to the provider's OAuth consent screen —
    // never returns here; control resumes at OAuth2StorageProviderCallback.vue.
    await connect(provider);
  } catch (error) {
    console.error(error);
    isConnecting.value = false;
  }
}

function close() { emit("close"); }
</script>

<template>
  <div class="fixed inset-0 bg-black/20 dark:bg-black/50 z-[450] flex items-center justify-center p-4" @click.self="close">
    <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[420px] overflow-hidden">
      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0">Add document source</h2>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="flex flex-col">
        <button
          v-for="provider in PROVIDERS"
          :key="provider.type"
          :disabled="isConnecting"
          class="flex items-center gap-3 px-6 py-4 border-0 border-b border-slate-100 dark:border-slate-800 last:border-0 bg-transparent cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
          @click="onSelect(provider.type)"
        >
          <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-50 dark:bg-slate-800 shrink-0">
            <svg v-if="provider.type === CLOUD_STORAGE.GOOGLE_DRIVE" viewBox="0 0 24 21" width="20" height="18">
              <path d="M7.5 13.5H22l-3.5 6H4L7.5 13.5z" fill="#34A853" />
              <path d="M4 19.5L7.5 13.5 12 6 16.5 13.5H7.5L4 19.5z" fill="#4285F4" />
              <path d="M12 6l4.5 7.5H22L17.5 6H12z" fill="#FBBC05" />
              <path d="M7.5 13.5l-3.5 6L0 14 4.5 7 7.5 13.5z" fill="#EA4335" />
            </svg>
            <svg v-else-if="provider.type === CLOUD_STORAGE.ONE_DRIVE" viewBox="0 0 24 18" width="22" height="16" fill="none">
              <path d="M14 12H5.5a3.5 3.5 0 0 1 0-7h.1A5 5 0 0 1 14.9 3 4 4 0 0 1 19 7v.5" stroke="#0078D4" stroke-width="1.5" stroke-linecap="round" />
              <path d="M8 14.5A3.5 3.5 0 0 0 11.5 18h7a3.5 3.5 0 0 0 0-7H11.5A3.5 3.5 0 0 0 8 14.5z" fill="#0078D4" />
            </svg>
            <svg v-else-if="provider.type === CLOUD_STORAGE.BOX_DRIVE" viewBox="0 0 24 24" width="18" height="18">
              <path d="M6 8.5A3.5 3.5 0 0 1 9.5 5h1.6a3.5 3.5 0 0 1 2.68 1.25l.44.53a1.5 1.5 0 0 0 1.15.54H16.5A3.5 3.5 0 0 1 20 10.82v5.68A3.5 3.5 0 0 1 16.5 20h-9A3.5 3.5 0 0 1 4 16.5v-6A2 2 0 0 1 6 8.5z" fill="#0061D5" />
            </svg>
            <svg v-else viewBox="0 0 24 22" width="18" height="16">
              <path d="M6.5 2L0 6.25l6.5 4.25L13 6.25 6.5 2z" fill="#0061FF" />
              <path d="M19.5 2L13 6.25l6.5 4.25 6.5-4.25L19.5 2z" fill="#0061FF" />
              <path d="M0 14.75L6.5 19l6.5-4.25L6.5 10.5 0 14.75z" fill="#0061FF" />
              <path d="M13 14.75L19.5 19l6.5-4.25-6.5-4.25-6.5 4.25z" fill="#0061FF" />
              <path d="M6.5 20.5L13 16.25l6.5 4.25-6.5 4.25-6.5-4.25z" fill="#0061FF" />
            </svg>
          </div>
          <span class="text-sm font-semibold text-slate-800 dark:text-slate-100">{{ provider.title }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
